import { useTranslation } from 'react-i18next';
import { Repeat, CalendarRange, CalendarDays, User } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { AvailabilityMode } from '../hooks/useAvailabilityWizard';
import type { ProviderOption } from '../../CreateAvailabilityDialog';
import { ContextualHint, ModeCard } from '../components';

interface ModeAndServicesStepProps {
  mode: AvailabilityMode;
  onModeChange: (mode: AvailabilityMode) => void;
  providers?: ProviderOption[];
  selectedProviderId: number | null;
  onProviderIdChange: (id: number | null) => void;
}

const MODE_OPTIONS: {
  value: AvailabilityMode;
  icon: typeof Repeat;
  titleKey: string;
  descriptionKey: string;
  hintKey: string;
  recommended?: boolean;
}[] = [
  {
    value: 'recurring',
    icon: Repeat,
    titleKey: 'availability.modes.regularHours',
    descriptionKey: 'availability.modes.regularHoursDesc',
    hintKey: 'availability.modes.regularHoursHint',
    recommended: true,
  },
  {
    value: 'date_range',
    icon: CalendarRange,
    titleKey: 'availability.modes.multipleDays',
    descriptionKey: 'availability.modes.multipleDaysDesc',
    hintKey: 'availability.modes.multipleDaysHint',
  },
  {
    value: 'one_time',
    icon: CalendarDays,
    titleKey: 'availability.modes.extraSlot',
    descriptionKey: 'availability.modes.extraSlotDesc',
    hintKey: 'availability.modes.extraSlotHint',
  },
];

export function ModeAndServicesStep({
  mode,
  onModeChange,
  providers = [],
  selectedProviderId,
  onProviderIdChange,
}: ModeAndServicesStepProps) {
  const { t } = useTranslation('staff');

  // Helper to get initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Provider selection (for admin). With a single provider there is
          nothing to choose: the wizard selects it and the field stays hidden. */}
      {providers.length > 1 && (
        <div className="space-y-2">
          <Label variant="section" className="flex items-center gap-2">
            <User className="w-4 h-4 text-gray-500" />
            {t('availability.dialog.staffMember', 'Staff Member')}
          </Label>
          <Select
            value={selectedProviderId?.toString() || ''}
            onValueChange={(value) => onProviderIdChange(Number(value))}
          >
            <SelectTrigger className="min-h-[48px]">
              <SelectValue placeholder={t('availability.dialog.selectStaff', 'Select staff member')}>
                {selectedProviderId && providers.find((p) => p.id === selectedProviderId) && (
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarImage
                        src={
                          providers.find((p) => p.id === selectedProviderId)?.avatar_url || undefined
                        }
                      />
                      <AvatarFallback className="text-xs">
                        {getInitials(
                          providers.find((p) => p.id === selectedProviderId)?.name || ''
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <span>{providers.find((p) => p.id === selectedProviderId)?.name}</span>
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {providers.map((provider) => (
                <SelectItem key={provider.id} value={provider.id.toString()}>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={provider.avatar_url || undefined} />
                      <AvatarFallback className="text-xs">{getInitials(provider.name)}</AvatarFallback>
                    </Avatar>
                    <span>{provider.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Mode selection */}
      <div className="space-y-3">
        <Label variant="section">
          {t('availability.wizard.modeSelection.title', 'What type of availability?')}
        </Label>

        <div className="space-y-3">
          {MODE_OPTIONS.map((option) => {
            const isSelected = mode === option.value;
            const title = t(option.titleKey, option.titleKey.split('.').pop());
            const description = t(option.descriptionKey, option.descriptionKey.split('.').pop());
            const hint = t(option.hintKey, option.hintKey.split('.').pop());

            return (
              <ModeCard
                key={option.value}
                icon={option.icon}
                title={
                  option.recommended
                    ? `${title} ${t('availability.wizard.modeSelection.recommended', '(Recommended)')}`
                    : title
                }
                description={description}
                hint={isSelected ? hint : undefined}
                selected={isSelected}
                onSelect={() => onModeChange(option.value)}
              />
            );
          })}
        </div>
      </div>

    </div>
  );
}
